import { useState } from 'react'
import { Button, Message, Modal, Collapse } from '@arco-design/web-react'
import { resetModelRatioApi } from '@/api/option'
import type { OptionsAPI } from '../useOptions'
import {
  JsonOptionSection,
  validateJsonNumberObject,
  type JsonOptionFieldDef,
} from './jsonOptionSection'
import JsonField from '../JsonField'
import { READONLY_OPTION_KEYS } from '../optionMeta'

const MODEL_JSON_FIELDS: JsonOptionFieldDef[] = [
  {
    key: 'ModelPrice',
    label: '模型固定价格',
    extraText: '一次调用消耗多少刀，优先级大于模型倍率',
    placeholder:
      '为一个 JSON 文本，键为模型名称，值为一次调用消耗多少刀，比如 "gpt-4-gizmo-*": 0.1，一次消耗0.1刀',
    validate: (v) => validateJsonNumberObject(v, 'ModelPrice'),
  },
  {
    key: 'ModelRatio',
    label: '模型倍率',
    placeholder: '为一个 JSON 文本，键为模型名称，值为倍率',
    validate: (v) => validateJsonNumberObject(v, 'ModelRatio'),
  },
  {
    key: 'CacheRatio',
    label: '提示缓存倍率',
    placeholder: '为一个 JSON 文本，键为模型名称，值为倍率',
    validate: (v) => validateJsonNumberObject(v, 'CacheRatio'),
  },
  {
    key: 'CreateCacheRatio',
    label: '缓存创建倍率',
    extraText: '默认为 5m 缓存创建倍率；1h 缓存创建倍率按固定乘法自动计算（当前为 1.6x）',
    placeholder: '为一个 JSON 文本，键为模型名称，值为倍率',
    validate: (v) => validateJsonNumberObject(v, 'CreateCacheRatio'),
  },
  {
    key: 'CompletionRatio',
    label: '模型补全倍率（仅对自定义模型有效）',
    extraText: '仅对自定义模型有效',
    placeholder: '为一个 JSON 文本，键为模型名称，值为倍率',
    validate: (v) => validateJsonNumberObject(v, 'CompletionRatio'),
  },
  {
    key: 'ImageRatio',
    label: '图片输入倍率（仅部分模型支持该计费）',
    extraText: '图片输入相关的倍率设置，键为模型名称，值为倍率，仅部分模型支持该计费',
    placeholder: '为一个 JSON 文本，键为模型名称，值为倍率，例如：{"gpt-image-1": 2}',
    validate: (v) => validateJsonNumberObject(v, 'ImageRatio'),
  },
  {
    key: 'AudioRatio',
    label: '音频倍率（仅部分模型支持该计费）',
    extraText: '音频输入相关的倍率设置，键为模型名称，值为倍率',
    placeholder: '为一个 JSON 文本，键为模型名称，值为倍率，例如：{"gpt-4o-audio-preview": 16}',
    validate: (v) => validateJsonNumberObject(v, 'AudioRatio'),
  },
  {
    key: 'AudioCompletionRatio',
    label: '音频补全倍率（仅部分模型支持该计费）',
    extraText: '音频输出补全相关的倍率设置，键为模型名称，值为倍率',
    placeholder: '为一个 JSON 文本，键为模型名称，值为倍率，例如：{"gpt-4o-realtime": 2}',
    validate: (v) => validateJsonNumberObject(v, 'AudioCompletionRatio'),
  },
]

export default function ModelRatioJsonSection({ options }: { options: OptionsAPI }) {
  const metaRaw = options.get('CompletionRatioMeta', '{}')
  const [metaExpanded, setMetaExpanded] = useState(false)

  const resetModelRatio = () => {
    Modal.confirm({
      title: '确定重置模型倍率吗？',
      content:
        '此操作会请求后端重置默认 ModelRatio，且不可撤销。当前页未保存的本地 JSON 编辑也会丢失，请确认后再继续。',
      okButtonProps: { status: 'danger' },
      onOk: async () => {
        try {
          await resetModelRatioApi()
          Message.success('模型倍率已重置')
          await options.reload()
        } catch (err) {
          Message.error(err instanceof Error ? err.message : '重置失败')
        }
      },
    })
  }

  return (
    <div className="model-ratio-json-section space-y-3">
      <div className="settings-toolbar">
        <div className="settings-info-strip flex-1">
          高级 JSON，适合批量粘贴或排障；日常运营请优先使用「价格设置」Tab。
        </div>
        <Button status="danger" size="small" onClick={resetModelRatio}>
          重置模型倍率
        </Button>
      </div>

      <Collapse
        bordered={false}
        activeKey={metaExpanded ? ['meta'] : []}
        onChange={(keys) => setMetaExpanded(Array.isArray(keys) && keys.includes('meta'))}
        className="settings-advanced-collapse settings-meta-collapse"
      >
        <Collapse.Item header="查看后端补全倍率锁定来源" name="meta">
          <JsonField value={metaRaw} onChange={() => undefined} rows={4} readOnly />
          <div className="settings-section-desc mt-2">
            后端合成字段，展示补全倍率锁定来源；键名 {Array.from(READONLY_OPTION_KEYS).join('、')} 不会写入 PUT。
          </div>
        </Collapse.Item>
      </Collapse>

      <JsonOptionSection
        options={options}
        fields={MODEL_JSON_FIELDS}
        switchFields={[{ key: 'ExposeRatioEnabled', label: '暴露倍率接口' }]}
        saveLabel="保存模型倍率设置"
        toolbarPlacement="bottom"
      />
    </div>
  )
}
